'use client';

import React, {
  ReactNode,
  useEffect,
  useState,
  useCallback,
  createContext,
  useContext,
} from 'react';

const reCaptchaKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_INVISIBLE;

declare global {
  interface Window {
    handleRecaptchaCallback: (token: string) => void;
    grecaptcha: {
      reset: () => void;
      execute: () => void;
    };
  }
}

interface RecaptchaProviderProps {
  children: ReactNode;
}

type RecaptchaValue = string;

const RecaptchaContext = createContext<RecaptchaValue | null>(null);

export const RecaptchaProvider = ({ children }: RecaptchaProviderProps) => {
  const [reCaptchaToken, setReCaptchaToken] = useState<string | null>(null);

  const handleRecaptchaCallback = useCallback((token: string) => {
    setReCaptchaToken(token);
  }, []);

  useEffect(() => {
    window.handleRecaptchaCallback = handleRecaptchaCallback;
  }, []);

  return (
    <>
      <RecaptchaContext.Provider value={reCaptchaToken}>{children}</RecaptchaContext.Provider>
      <div
        className="g-recaptcha"
        data-sitekey={reCaptchaKey}
        data-callback="handleRecaptchaCallback" // 讓 Google reCaptcha 設定 reCaptchaToken state
        data-size="invisible"
        style={{ display: 'none' }}
      />
    </>
  );
};

export const useRecaptcha = () => {
  const token = useContext(RecaptchaContext);
  const [grecaptcha, setGrecaptcha] = useState<Window['grecaptcha'] | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      if (window.grecaptcha && typeof window.grecaptcha?.execute === 'function') {
        clearInterval(interval);
        setGrecaptcha(window.grecaptcha);
        window.grecaptcha.execute();
      }
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const reset = () => {
    console.log('reCaptcha Reset');
    grecaptcha?.reset();
  };

  const execute = () => {
    console.log('reCaptcha Execute');
    grecaptcha?.execute();
  };

  return { token, reset, execute };
};
